import * as React from 'react';

import type {
	W_FieldReport_FindAllReportTypesForCompany_VM_Row as ReportTypeRow,
} from 'ab-api/web/fieldReport/findAllReportTypesForCompany';

import { ReportTypeStatus } from '@acceligentllc/shared/enums/reportTypeStatus';

import Radio from 'af-components/Controls/Radio';
import Tooltip from 'af-components/Tooltip';

import { ReportTypeDoNotUseMode } from '.';
import ReportTypeBlocks from './ReportTypeBlocks';
import { selectableFieldTypes } from './ReportTypeBlocks';
import styles from './styles.module.scss';

export type SelectedFieldRef = {
	reportTypeId: number;
	reportBlockId: number;
	reportBlockFieldId: number;
};

interface Props {
	reportTypes: ReportTypeRow[];
	selectedFields: SelectedFieldRef[];
	onSelectField: (rtId: number, rbId: number, rbfId: number) => void;
	onMarkDoNotUse: (rtId: number, keepPreviousDataSources: boolean) => void;
	searchTerm: string;
	exclusionModeByLatestId: Map<number, ReportTypeDoNotUseMode>;
	openRevisionsFor: (latestRtId: number) => void;
	outdatedLatestIds: number[];
	hasAssociatedOldRevisionByLatestId: Map<number, boolean>;
}

const TOOLTIP_MESSAGE_SELECTED = <div className={styles.tooltip}>
	<b>Field Selected for Job Tracking</b>
	A field has been selected in this block
	for Job Tracking. This block will be used in a template.
</div>;

const TOOLTIP_MESSAGE_WARNING = <div className={styles.tooltip}>
	<b>Missing Association</b>
	This report type has revisions with missing associations. Please resolve them in the Revisions modal before saving.
</div>;

const ReportTypeCards: React.FC<Props> = (props: Props) => {
	const {
		reportTypes,
		selectedFields,
		onSelectField,
		onMarkDoNotUse,
		searchTerm,
		exclusionModeByLatestId,
		openRevisionsFor,
		outdatedLatestIds,
		hasAssociatedOldRevisionByLatestId,
	} = props;

	const selectedByRt = React.useMemo(() => {
		const map = selectedFields.reduce<Map<number, SelectedFieldRef[]>>((acc, field) => {
			const arr = acc.get(field.reportTypeId) ?? [];
			arr.push(field);
			acc.set(field.reportTypeId, arr);
			return acc;
		}, new Map());
		return map;
	}, [selectedFields]);

	const renderBlocks = React.useCallback(
		(rt: ReportTypeRow, selectedForRt: SelectedFieldRef[]) => {
			return (
				<ReportTypeBlocks
					onSelectField={onSelectField}
					reportType={rt}
					searchTerm={searchTerm}
					selectedFields={selectedForRt}
				/>
			);
		},
		[onSelectField, searchTerm]
	);

	const onCheck = React.useCallback(
		(reportTypeId: number, keepPreviousDataSources: boolean) => () => {
			onMarkDoNotUse(reportTypeId, keepPreviousDataSources);
		},
		[onMarkDoNotUse]
	);

	const handleCheckDoNotUse = React.useCallback((rt: ReportTypeRow) => () => {
		const mode = exclusionModeByLatestId.get(rt.id) ?? ReportTypeDoNotUseMode.NONE;
		if (mode === ReportTypeDoNotUseMode.NONE) {
			onMarkDoNotUse(rt.id, false);
		}
	}, [exclusionModeByLatestId, onMarkDoNotUse]);

	const handleViewRevisions = React.useCallback((rt: ReportTypeRow) => () => {
		openRevisionsFor(rt.id);
	}, [openRevisionsFor]);

	/**
	 * Render the "Do not use" controls for a given latest RT:
	 * - If hasAssociatedOldRevisionByLatestId === false
	 */
	const renderExclusionControls = React.useCallback(
		(rt: ReportTypeRow) => {
			const mode = exclusionModeByLatestId.get(rt.id) ?? ReportTypeDoNotUseMode.NONE;
			const canSplit = hasAssociatedOldRevisionByLatestId.get(rt.id) ?? false;

			if (!canSplit) {
				// Single revision family or template creation: a single toggle.
				const checked = mode !== ReportTypeDoNotUseMode.NONE;

				return (
					<Radio
						checked={checked}
						label="Do not use"
						onCheck={handleCheckDoNotUse(rt)}
					/>
				);
			}

			// Multi-revision family: keep/clear as before.
			return (
				<>
					<Radio
						checked={mode === ReportTypeDoNotUseMode.KEEP}
						label="Do not use (but keep any data)"
						onCheck={onCheck(rt.id, true)}
					/>
					<Radio
						checked={mode === ReportTypeDoNotUseMode.CLEAR}
						label="Do not use (and clear data)"
						onCheck={onCheck(rt.id, false)}
					/>
				</>
			);
		},
		[exclusionModeByLatestId, hasAssociatedOldRevisionByLatestId, onCheck, handleCheckDoNotUse]
	);

	const filteredReportTypes = React.useMemo(() => {
		const term = searchTerm.trim().toLowerCase();

		let _reportTypes = reportTypes;
		if (term) {
			_reportTypes = reportTypes.filter((rt) => {
				if (rt.name.toLowerCase().includes(term)) {
					return true;
				}
				for (const block of rt.reportTypeBlocks) {
					if (block.reportBlock.name.toLowerCase().includes(term)) {
						return true;
					}
					for (const field of block.reportBlock.fields) {
						if (!selectableFieldTypes.includes(field.fieldType)) {
							continue;
						}
						if (field.name.toLowerCase().includes(term)) {
							return true;
						}
					}
				}

				return false;
			});
		}

		// Put required ones on top
		return _reportTypes.sort((a, b) => {
			if (a.isRequired === b.isRequired) {
				return 0;
			}
			return a.isRequired ? -1 : 1;
		});

	}, [reportTypes, searchTerm]);

	return (
		<div className={styles['cards-scroll']}>
			<div className={styles['cards-grid']}>
				{filteredReportTypes.map((rt) => {
					const selectedForRt = selectedByRt.get(rt.id) ?? [];
					const isOutdated = (outdatedLatestIds ?? []).includes(rt.id);

					const cardClass =
						`${styles['card-rt']} ` +
						(isOutdated
							? styles['card-rt-outdated']
							: selectedForRt.length
								? styles['selected-report-type']
								: '');

					return (
						<div className={cardClass} key={rt.id}>
							{isOutdated && (
								<Tooltip message={TOOLTIP_MESSAGE_WARNING} >
									<span
										className={`${styles['card-warn-corner']} icon-warning`}
									/>
								</Tooltip>
							)}
							<div className={styles['card-header']}>
								<div className={styles['card-title']}>
									{rt.name}
									{
										rt.status === ReportTypeStatus.INACTIVE &&
										!rt.isDeleted &&
										<span className={styles['card-inactive']}>Inactive</span>
									}
									{
										rt.isDeleted &&
										<span className={styles['card-inactive']}>Deleted</span>
									}
									{selectedForRt.length > 0 && !isOutdated && (
										<Tooltip message={TOOLTIP_MESSAGE_SELECTED} >
											<span
												className={`${styles['check-icon']} icon-check`}
											/>
										</Tooltip>
									)}
								</div>
								<div
									className={styles['revision-link']}
									onClick={handleViewRevisions(rt)}
								>
									View Report Type Revisions
								</div>

								{renderExclusionControls(rt)}
							</div>
							<div className={styles['card-body']}>
								{renderBlocks(rt, selectedForRt)}
							</div>
						</div>
					);
				})}
			</div>
		</div>
	);
};

export default React.memo(ReportTypeCards);
